/* eslint-disable @typescript-eslint/await-thenable */
import ssf from '@elliemae/em-ssf-guest';
import { WarningTriangle } from '@elliemae/ds-icons';
import { TooltipTextProvider } from '@elliemae/ds-truncated-tooltip-text';
import {
  PricingSearchNonLoanValues,
  useLookupOptions,
  deriveAccordionFieldMapFromSchema,
  applyClientLookupsToSchema,
  useRawLookupOptions,
  useLoanDataFieldSelectorHook,
} from '@frontend/pricing-search';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import {
  dispatchOriginData,
  dispatchUserData,
  getInitData,
  IS_USER_CANNOT_BE_FOUND,
  IS_DISABLED_USER,
  IS_INVALID_CREDENTIAL,
  IS_INVALID_CREDS,
  IS_INVALID_TOKEN,
  IS_RECONCILE,
  setMockInitData,
} from '../common/app-helpers';
import Common from '../common/services/Common';
import Session, {
  ALL_SUMMARY_PARAMS,
  APPLICATION_NAME,
  IS_BUYSIDE,
  IS_JWT_TOKEN,
  IS_LO,
  IS_LOCK_EXTENSION,
  IS_SCENARIO_MODE,
  IS_SELLSIDE,
  IS_STANDALONE,
  IS_VALIDATE,
  ORIGIN_TRANSACTION_TYPE,
} from '../common/services/Session';
import { buysideRequested } from '../common/utils/constants/ErrorCodes';
import { startLog } from '../common/utils/logger';
import {
  openGlobalErrorModalAction,
  openNoHELOCInitialDrawErrorModal,
  openNoLoanAmountErrorModal,
} from '../data/globalErrorModal/actionCreators';
import {
  useLoanInformationSelector,
  usePricingSearchConfiguration,
} from '../data/useSelectors';
import { lookupsActions } from '../store/lookups/index';
import { LockConfirmWrapper } from '../view/LockConfirm/LockConfirmWrapper';
import { LockExtension } from '../view/LockExtension/LockExtension';
import { LockSummary } from '../view/LockSummary/LockSummary';
import { Login } from '../view/Login';
import { SearchForm } from '../view/SearchForm/SearchForm';
import { SearchResult } from '../view/SearchResult';
import {
  ScenarioWrapper,
  ScenarioChannelSelect,
  ScenarioSearch,
  ScenarioResults,
  ScenarioSummary,
} from '../view/Scenario';
import { GlobalErrorModal } from '../view/components/GlobalErrorModal';
import { Header } from '../view/components/Header/index';
import { ScreenLoader } from '../view/components/ScreenLoader';
import {
  StyledEppsContainerBody,
  StyledEppsContainerHeader,
  StyledEppsParentContainer,
} from '../view/global/styles/styled';
import {
  PATH_LOCK_EXTENSION,
  PATH_LOCK_SUMMARY,
  PATH_LOGIN,
  PATH_SEARCH_FOR_PRODUCTS,
  PATH_SEARCH_RESULTS,
  PATH_SCENARIO,
  PATH_SCENARIO_CHANNEL,
} from './constants';
import { setLoanDataValue } from '../view/components/Form/utils/common';
import { getZeroBasedPricingSelector } from '../data/lockSummary/actionCreators';
import { useIsZeroBasedPricing } from '../common/utils/customHooks/useIsZeroBasedPricing';
import { getUserRightsWithIds } from '../common/services/Rights';
import api from '../common/utils/api';
import { useSmartClientLoanUpdate } from '../common/services/useSmartClientLoanUpdate';
import { closeLoader } from '../data/screenLoader/actions';

const App: React.FC = () => {
  const [moveNext, setMoveNext] = useState(false);
  const [isError, setIsError] = useState(false);
  const [initData, setInitData] = useState(undefined);
  const [roleName, setRoleName] = useState('');
  const [canAssignLoanOfficer, setCanAssignLoanOfficer] = useState(true);
  const [viewDeliveryAndCommitment, setViewDeliveryAndCommitment] =
    useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false); // State to track login status
  const navigate = useNavigate();
  const isBuyside = Session.get(IS_BUYSIDE);
  const isSellside = Session.get(IS_SELLSIDE);
  const isValidate = Session.get(IS_VALIDATE);
  const isJWTToken = Session.get(IS_JWT_TOKEN);
  const isScenarioMode = Session.get(IS_SCENARIO_MODE);
  const isTPO = Session.get(APPLICATION_NAME) === 'TPO';
  const loanData = useLoanDataFieldSelectorHook() as any;
  const {
    triggerRefreshPartnerUi,
    refreshUI,
    subscribeToLoanModalChange,
    unsubscribeLoanModalChange,
  } = useSmartClientLoanUpdate();
  const isSmartClient = Session.get(APPLICATION_NAME) === 'ENC';

  const isLO = Session.get(IS_LO);
  const dispatch = useDispatch();
  const rawGovtUpfrontFees = useLookupOptions('fee');
  const govtUpfrontFees = useMemo(
    () => rawGovtUpfrontFees || [],
    [rawGovtUpfrontFees],
  );
  const customFields = useLookupOptions('customFields');
  const isZeroBasedPricing = useIsZeroBasedPricing();

  const fetchData = async (isRefresh = false) => {
    const data = await getInitData(
      dispatch,
      isRefresh ?? false,
      setIsError,
      undefined,
    );
    if (data?.originData?.loanData?.scenarioTypeID) {
      Session.set(IS_SCENARIO_MODE, true);
      // setMoveNext(true);
    } else {
      Session.set(IS_SCENARIO_MODE, false);
    }
    const categories = await api.getAttributionCategories();
    data.attributionCategories = categories.attributionCategoryResponses;
    if (data.code === undefined) {
      dispatchUserData(dispatch);
      await setUserClientRoles();
      dispatch(lookupsActions.getLookupsDataAction({ dispatch } as any));
    }
    dispatch(
      getZeroBasedPricingSelector(
        data?.originData?.originalLoanInformation?.channel,
      ) as any,
    );
    setInitData(data);
  };

  useEffect(() => {
    refreshPartnerUi();
  }, [triggerRefreshPartnerUi]);

  const refreshPartnerUi = async () => {
    startLog(buysideRequested);
    Session.set(ALL_SUMMARY_PARAMS, {});
    if (!Session.get(IS_STANDALONE)) {
      await fetchData(triggerRefreshPartnerUi > 0).then(() => {
        dispatch(closeLoader());
        // Refresh the component by toggling a state variable
        if (triggerRefreshPartnerUi > 0) {
          setMoveNext(false);
        }
      });
    }
  };

  useEffect(() => {
    if (isSmartClient) {
      subscribeToLoanModalChange(() => {
        console.log('Loan committed changed');
        refreshUI();
      });
    }
    // Unsubscribe the listener on unmount
    return () => {
      if (isSmartClient) {
        unsubscribeLoanModalChange();
      }
    };
  }, [isSmartClient]);

  useEffect(() => {
    if (isError) {
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }) as any,
      );
    }
  }, [isError]);

  const setUserClientRoles = async () => {
    const [, rightsResponse]: [
      void,
      {
        roleName: string;
        clientRoleId: number;
        defaultView: number;
        rights: Array<{ rightId: number; rightName: string }>;
      },
    ] = await Promise.all([Common.setUserRoles(), Common.setClientRoles()]);
    setRoleName(rightsResponse?.roleName);
    const rights = rightsResponse?.rights ?? [];
    setCanAssignLoanOfficer(rights.some(({ rightId }) => rightId === 28)); // 28 is the AssignLoanOfficer right
    setViewDeliveryAndCommitment(rights.some(({ rightId }) => rightId === 12));
    // if (!rightsResponse?.rights) {
    //   dispatch(
    //     openGlobalErrorModalAction({
    //       title: 'You have not been assigned a role!',
    //       errorMessage:
    //         'You will have limited access to any features until you have been assigned a role. Please contact your administrator.',
    //       icon: <WarningTriangle size="xxl" color={['warning', '600']} />,
    //     }) as any,
    //   );
    // }
  };

  useEffect(() => {
    if (initData) {
      const {
        notTokenCredResource,
        errorLoanAmountBlank,
        hasLoanAmount,
        loanAmountBlank,
        lienPosition,
        originData,
        code,
        isRedirectToError,
        isHELOCLoan,
        hasHELOCInitialDraw,
        attributionCategories,
      } = initData;
      if (!notTokenCredResource) {
        if (isHELOCLoan && !hasHELOCInitialDraw && !isScenarioMode) {
          dispatch(openNoHELOCInitialDrawErrorModal() as any);
        } else if (
          !isHELOCLoan &&
          !isScenarioMode &&
          (errorLoanAmountBlank || !hasLoanAmount)
        ) {
          dispatch(openNoLoanAmountErrorModal() as any);
        }
        if (
          (!loanAmountBlank &&
            lienPosition &&
            (hasHELOCInitialDraw || (!isHELOCLoan && hasLoanAmount)) &&
            govtUpfrontFees.length > 0) ||
          isScenarioMode
        ) {
          dispatchOriginData(
            dispatch,
            originData,
            govtUpfrontFees,
            customFields,
            isZeroBasedPricing,
            attributionCategories,
          );
          setMoveNext(true);
        }
      }
      if (initData[IS_RECONCILE] === IS_RECONCILE) {
        fetchData(true);
      }
      if (isRedirectToError) {
        setMoveNext(true);
        setIsError(true);
      }
      if (
        code &&
        code !== IS_INVALID_TOKEN &&
        code !== IS_INVALID_CREDENTIAL &&
        code !== IS_INVALID_CREDS &&
        code !== IS_DISABLED_USER &&
        code !== IS_USER_CANNOT_BE_FOUND
      ) {
        throw new Error();
      }
      // add custom fields to schema
    }
  }, [initData, govtUpfrontFees, isZeroBasedPricing]);

  useEffect(() => {
    if (moveNext) {
      // Check if in Scenario mode and redirect to Scenario flow
      if (Session.get(IS_SCENARIO_MODE)) {
        navigate(PATH_SCENARIO_CHANNEL);
      } else if (!isLO && !isJWTToken) {
        navigate(PATH_LOGIN);
      } else if (Session.get(IS_LOCK_EXTENSION)) {
        navigate(PATH_LOCK_EXTENSION);
      } else if (Session.get(IS_BUYSIDE) || Session.get(IS_SELLSIDE)) {
        navigate(PATH_LOCK_SUMMARY);
      } else {
        navigate(PATH_SEARCH_RESULTS);
      }
    }
  }, [moveNext]);

  // Callback function to handle successful login
  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    navigate('/search-results');
  };

  const allLookups = useRawLookupOptions();
  const loanInfo = (useLoanInformationSelector() || {}) as any;
  const schema = usePricingSearchConfiguration();
  const schemaWithLookups = useMemo(() => {
    if (!schema) return schema;
    return applyClientLookupsToSchema(schema, allLookups);
  }, [schema, allLookups]);

  const schemaForm = useForm<any>({
    mode: 'onSubmit',
    defaultValues: {},
    resetOptions: { keepValues: false },
  });

  const [activeAccordions, setActiveAccordions] = useState<
    Record<number, boolean>
  >({});

  const accordionFieldMap = useMemo(
    () => deriveAccordionFieldMapFromSchema(schemaWithLookups),
    [schemaWithLookups],
  );

  useEffect(() => {
    if (loanInfo && roleName && Object.keys(loanInfo).length > 0) {
      const userRights = getUserRightsWithIds() || [];
      const clientSettings = allLookups?.clientSettings?.[0] || {};
      const encompassSettings = clientSettings?.encompassSettings || {};
      const options = clientSettings?.options || {};
      const nonLoanValues: PricingSearchNonLoanValues = {
        role: roleName ?? '',
        canAssignLoanOfficer,
        viewDeliveryAndCommitment,
        isBuyside,
        isSellside,
        isValidate,
        lockStatus: '',
        isTPO,
        loanChannel: loanInfo?.loanInformation?.loanChannel,
        userRights,
        requestLockType: loanInfo?.requestLockType,
        // EPPS Options
        skipIncome: options?.skipIncome,
        skipAssets: options?.skipAssets,
        allowLock: options?.allowLock,
        roundCorr: options?.roundCorr,
        roundBroker: options?.roundBroker,
        noClosingCost: options?.noClosingCost,
        flagged: options?.flagged,
        hideLockRequest: options?.hideLockRequest,
        icePpeFloat: options?.icePpeFloat,
        isMIQuotesEnabled: options?.isMIQuotesEnabled,
        isBuySideHistorical: options?.isBuySideHistorical,
        changeEffectiveDateOnReLock:
          encompassSettings?.changeEffectiveDateOnReLock,
        changeProgramOnReLock: encompassSettings?.changeProgramOnReLock,
        changeAdditionalLoanProg: encompassSettings?.changeAdditionalLoanProg,
      };
      Object.assign(loanInfo, {
        nonLoanValues,
      });
      schemaForm.reset(loanInfo);
      schemaForm.setValue('nonLoanValues.role', roleName ?? '');
    }
  }, [
    loanInfo,
    schemaForm,
    roleName,
    canAssignLoanOfficer,
    isBuyside,
    isSellside,
    isValidate,
    allLookups,
  ]);

  const renderContent = useMemo(() => {
    return (
      <TooltipTextProvider>
        <StyledEppsContainerBody>
          {moveNext && (
            <Routes>
              <Route path={PATH_SCENARIO} element={<ScenarioWrapper />}>
                <Route path="Channel" element={<ScenarioChannelSelect />} />
                <Route
                  path="Search"
                  element={<ScenarioSearch schemaForm={schemaForm} />}
                />
                <Route path="Results" element={<ScenarioResults />} />
                <Route path="Summary" element={<ScenarioSummary />} />
              </Route>

              <Route
                path={PATH_SEARCH_FOR_PRODUCTS}
                element={
                  <SearchForm
                    schema={schemaWithLookups}
                    schemaForm={schemaForm}
                    activeAccordions={activeAccordions}
                    setActiveAccordions={setActiveAccordions}
                    accordionFieldMap={accordionFieldMap}
                  />
                }
              />
              <Route
                path={PATH_LOCK_SUMMARY}
                element={
                  isBuyside || isSellside ? (
                    <LockConfirmWrapper />
                  ) : (
                    <LockSummary />
                  )
                }
              />
              <Route path={PATH_LOCK_EXTENSION} element={<LockExtension />} />

              {/* Login Route */}
              <Route
                path="/login"
                element={<Login onLoginSuccess={handleLoginSuccess} />} // Pass the callback
              />

              {/* Search Results Route */}
              <Route path="/search-results" element={<SearchResult />} />
            </Routes>
          )}
        </StyledEppsContainerBody>
      </TooltipTextProvider>
    );
  }, [isLoggedIn, moveNext, activeAccordions, isBuyside, isSellside]);

  const Landing = (Content: JSX.Element) => {
    return (
      <>
        <StyledEppsParentContainer>
          {!isScenarioMode && (
            <StyledEppsContainerHeader>
              <Header
                schema={schemaWithLookups}
                schemaForm={schemaForm}
                setActiveAccordions={setActiveAccordions}
                accordionFieldMap={accordionFieldMap}
              />
            </StyledEppsContainerHeader>
          )}
          {Content}
        </StyledEppsParentContainer>
        <ScreenLoader />
        <GlobalErrorModal />
      </>
    );
  };

  return Landing(renderContent);
};

export default App;
